import React, { useEffect } from "react";
import type { FC, PropsWithChildren } from "react";
import ReactQuill from "react-quill";
import { renderToStaticMarkup } from "react-dom/server";
import { useDeleteNote, useUpdateNote } from "../../hooks/patients";
import toast from "react-hot-toast";
import useNoteTypeOptions, {
  NoteTypeOption,
} from "../../hooks/useNoteTypeOptions";
import CustomDropdown from "../../components/CustomDropdown/CustomDropdown";

import { INote, IPatient } from "../../hooks/patients/index.type";
import {
  ModalForm,
  Modal,
  Button,
  ModalHeading,
  RadioButton,
  InputsSection,
  ModalButtons,
} from "../../components/Modal";

import "react-quill/dist/quill.snow.css";
import classes from "../../styles/CustomStyles.module.css";
import NoteSkeleton from "../../components/Skeletons/NoteSkeleton";
import useAddNote from "../../hooks/patients/useAddNote";
import styles from "../../components/PhoneCallTemplate/PhoneCallTemplate.module.css";

export interface IAddNoteStates {
  noteCategory: {
    value: "clinical" | "non-clinical" | "timer-summary";
    error: string;
  };
  noteContent: { value: string; error: string; originalValue?: string };
  selectedNoteId?: string;
}

type AddNotesModalProps = PropsWithChildren<{
  addNotesModal: boolean;
  saveNoteLoading?: boolean;
  getNotesLoading?: boolean;
  patientData?: IPatient;
  fetchedNotes: INote[];
  onCloseModal?: () => void;
  noteModalStates: IAddNoteStates;
  setNoteModalStates: React.Dispatch<React.SetStateAction<IAddNoteStates>>;
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>
  ) => Promise<{ status: string; data?: any } | undefined>;
  getNotes: (reset?: boolean, sortOrder?: "asc" | "desc") => Promise<void>;
  noteTypeOptions: NoteTypeOption[];
  noteTypesLoading?: boolean;
  noteTypesError?: string;
  triggeredBy?: string;
  billingSessionId?: string;
}>;

const AddNotesModal: FC<AddNotesModalProps> = (props) => {
  const [sortOrder, setSortOrder] = React.useState("Latest First");
  const [deleteNoteModal, setDeleteNoteModal] = React.useState<{
    status: boolean;
    data?: INote;
  }>({ status: false });
  const [isAddingNote, setIsAddingNote] = React.useState(false);
  const [isUpdatingNote, setIsUpdatingNote] = React.useState(false);
  const [isPhoneCall, setIsPhoneCall] = React.useState(false);
  const [originalIsPhoneCall, setOriginalIsPhoneCall] = React.useState(false);
  const [isImportant, setIsImportant] = React.useState(false);
  const [selectedNoteType, setSelectedNoteType] = React.useState<string>("");
  const [lastUpdatedBy, setLastUpdatedBy] = React.useState<string>("");

  const { addNote, addNoteLoading } = useAddNote({
    patientId: props.patientData?._id,
  });

  const handleImportantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsImportant(e.target.checked);
  };

  const handlePhoneCallChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsPhoneCall(isChecked);

    if (isChecked) {
      let noteMessage = "";
      const selectedNoteId = props.noteModalStates.selectedNoteId;
      if (selectedNoteId) {
        const selectedNote = props.fetchedNotes.find(
          (note) => note._id === selectedNoteId
        );
        if (selectedNote) {
          noteMessage = selectedNote?.richText || "";
        }
      } else {
        noteMessage = props.noteModalStates.noteContent.value;
      }
      const phoneCallHtml = `
       <div class="${styles.phoneCallContainer}">
        <div class="${styles.phoneCallText}">
          ${noteMessage}
        </div>
        <br/><br/><br/>
        <div class="${styles.divider}">-------</div>
        <div class="${styles.phoneCallHeader}">Phone Call</div>
        <div class="${styles.timeLabel}">Start Time: </div>
        <div class="${styles.timeLabel}">End Time: </div>
        </div>
        `;

      props.setNoteModalStates((prevVal) => ({
        ...prevVal,
        noteContent: {
          ...prevVal.noteContent,
          value: phoneCallHtml,
          originalValue: prevVal.noteContent.value,
        },
      }));
    } else {
      const selectedNoteId = props.noteModalStates.selectedNoteId;
      if (selectedNoteId) {
        const selectedNote = props.fetchedNotes.find(
          (note) => note._id === selectedNoteId
        );
        if (selectedNote) {
          props.setNoteModalStates((prevVal) => ({
            ...prevVal,
            noteContent: {
              ...prevVal.noteContent,
              value: selectedNote.richText || "",
              originalValue: "",
            },
          }));
        }
      } else {
        props.setNoteModalStates((prevVal) => ({
          ...prevVal,
          noteContent: {
            ...prevVal.noteContent,
            value: prevVal.noteContent.originalValue || "",
            originalValue: "",
          },
        }));
      }
    }
  };
  const [selectedNoteLabel, setSelectedNoteLabel] =
    React.useState("Select a Note Type");
  // const {
  //   noteTypeOptions,
  //   loading: noteTypesLoading,
  //   error: noteTypesError,
  // } = useNoteTypeOptions();

  const { deleteNote, deleteNoteLoading } = useDeleteNote({
    patientId: props.patientData?._id,
  });
  const { updateNote } = useUpdateNote({ patientId: props.patientData?._id });

  const notesContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (props.addNotesModal) {
      setSortOrder("Latest First");
      props.getNotes(true, "desc");
    }
  }, [props.addNotesModal]);

  React.useEffect(() => {
    if (!props.noteModalStates.selectedNoteId) {
      setIsPhoneCall(false);
    }
  }, [props.noteModalStates.selectedNoteId]);

  React.useEffect(() => {
    if (props.addNotesModal) {
      props.setNoteModalStates((prevVal) => ({
        ...prevVal,
        noteContent: { value: "", error: "", originalValue: "" },
        noteCategory: {
          value: props.triggeredBy === "Stop" ? "timer-summary" : "clinical",
          error: "",
        },
        selectedNoteId: undefined,
      }));

      setIsPhoneCall(false);
    } else {
      setDeleteNoteModal({ status: false });
      setIsPhoneCall(false);
    }
  }, [props.addNotesModal, props.triggeredBy]);

  React.useEffect(() => {
    if (props.noteModalStates.noteCategory.value === "non-clinical") {
      setSelectedNoteType("");
      setSelectedNoteLabel("Select a Note Type");
    }
  }, [props.noteModalStates.noteCategory.value]);

  const QUILL_MODULES = {
    toolbar: [
      ["bold", "italic"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const QUILL_FORMATS = ["bold", "italic", "list", "bullet", "ordered"];

  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (isoString?: string): string => {
    if (!isoString) return "Date not available";
    const date = new Date(isoString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const resetFormState = () => {
    const currentCategory =
      props.noteModalStates.noteCategory?.value || "clinical";
    const isNonClinical = currentCategory === "non-clinical";

    props.setNoteModalStates((prevVal) => ({
      ...prevVal,
      noteContent: {
        value: "",
        error: "",
        originalValue: "",
      },
      selectedNoteId: undefined,
      noteCategory: {
        ...prevVal.noteCategory,
        value: currentCategory,
        error: "",
      },
    }));

    if (isNonClinical) {
      setSelectedNoteType("general-note");
      setSelectedNoteLabel("General notes");
    } else {
      setSelectedNoteType("");
      setSelectedNoteLabel("Select a Note Type");
    }

    setIsImportant(false);
    setIsPhoneCall(false);
    setOriginalIsPhoneCall(false);
  };

  const handleNoteClick = (note: INote) => {
    if (isUpdatingNote || isAddingNote) return;

    const isNonClinical = note.noteCategory === "non-clinical";

    const newState = {
      noteCategory: {
        value: note.noteCategory as
          | "clinical"
          | "non-clinical"
          | "timer-summary",
        error: "",
      },
      noteContent: {
        value: note.richText || "",
        error: "",
        originalValue: note.richText || "",
      },
      selectedNoteId: note._id,
    };

    props.setNoteModalStates((prevVal) => ({
      ...prevVal,
      ...newState,
    }));

    if (isNonClinical) {
      setSelectedNoteType("general-note");
      setSelectedNoteLabel("General notes");
    } else if (note.type) {
      setSelectedNoteType(note.type);
      const noteTypeOption = props.noteTypeOptions.find(
        (opt) => opt.key === note.type
      );
      setSelectedNoteLabel(noteTypeOption?.value || "Select a Note Type");
    } else {
      setSelectedNoteType("");
      setSelectedNoteLabel("Select a Note Type");
    }

    setIsImportant(note.isImportant === "yes");
    const phoneCallStatus = note.isPhoneCall === "yes";
    setIsPhoneCall(phoneCallStatus);
    setOriginalIsPhoneCall(phoneCallStatus);
  };

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        !props.getNotesLoading
      ) {
        props.getNotes(false, sortOrder === "Latest First" ? "desc" : "asc");
      }
    },
    [props.getNotesLoading, props.getNotes, sortOrder]
  );

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSortOrder = e.target.value;
    setSortOrder(newSortOrder);
    props.getNotes(true, newSortOrder === "Latest First" ? "desc" : "asc");
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      const response = await deleteNote(noteId);
      if (response?.status === "success") {
        await props.getNotes(true, "desc");

        if (props.noteModalStates.selectedNoteId === noteId) {
          resetFormState();
        }

        setDeleteNoteModal({ status: false });
      } else {
        toast.error("Failed to delete note");
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleDeleteConfirm = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (deleteNoteModal.data?._id) {
      await handleDeleteNote(deleteNoteModal.data._id);
    }
  };

  const handleUpdateNote = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    const plainText = stripHtmlTags(props.noteModalStates.noteContent.value);
    if (!plainText.trim()) {
      props.setNoteModalStates((prevVal) => ({
        ...prevVal,
        noteContent: {
          ...prevVal.noteContent,
          error: "Note content cannot be empty",
        },
      }));
      return;
    }

    try {
      if (!props.noteModalStates.selectedNoteId) {
        return await handleAddNote();
      } else {
        return await handleUpdateExistingNote(plainText);
      }
    } catch (error) {
      console.error("Error with note operation:", error);
      toast.error("Failed to save note");
      throw error;
    }
  };

  const handleAddNote = async () => {
    setIsAddingNote(true);
    try {
      const noteCategory =
        props.noteModalStates.noteCategory?.value || "clinical";
      const isNonClinical = noteCategory === "non-clinical";

      const noteContent = props.noteModalStates.noteContent.value;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = noteContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      const noteData: any = {
        note: plainText,
        richText: noteContent,
        noteCategory,
        isImportant: isImportant ? "yes" : "no",
        isPhoneCall: isPhoneCall ? "yes" : "no",
      };
      if (noteData.noteCategory === "timer-summary") {
        noteData.billingSessionId = props.billingSessionId || "";
      }

      if (!isNonClinical && selectedNoteType) {
        noteData.type = selectedNoteType;
      } else if (!isNonClinical && !selectedNoteType) {
        noteData.type = "general-note";
      }

      const response = await addNote(noteData);

      if (response?.status === "success") {
        // Update the last updated by from the response if available
        const clinicianName =
          (response as any)?.data?.note?.clinician?.name ||
          (response as any)?.data?.note?.[0]?.clinician?.name ||
          "Admin";
        setLastUpdatedBy(clinicianName);

        // Always refresh the notes list to get the latest data
        await props.getNotes(
          true,
          sortOrder === "Latest First" ? "desc" : "asc"
        );

        resetFormState();

        if (noteCategory === "timer-summary" && props.onCloseModal) {
          props.onCloseModal();
        }

        return response; // Return the response to be used by the parent component
      }
      return response;
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Failed to save note");
      throw error; // Re-throw the error to be handled by the parent component
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleUpdateExistingNote = async (plainText: string) => {
    setIsUpdatingNote(true);
    try {
      const isNonClinical =
        props.noteModalStates.noteCategory.value === "non-clinical";
      const formData: any = {
        _id: props.noteModalStates.selectedNoteId!,
        noteCategory: props.noteModalStates.noteCategory.value,
        note: plainText,
        richText: props.noteModalStates.noteContent.value,
        isImportant: isImportant ? "yes" : "no",
        isPhoneCall: isPhoneCall ? "yes" : "no",
      };

      if (!isNonClinical && selectedNoteType) {
        formData.type = selectedNoteType;
      } else if (!isNonClinical && !selectedNoteType) {
        formData.type = "general-note";
      }

      const response = await updateNote(formData);
      console.log("Update note response:", response);

      if (response?.status === "success") {
        // Update the last updated by from the response if available
        if (response?.data?.note?.[0]?.clinician?.name) {
          setLastUpdatedBy(response.data.note[0].clinician.name);
        }

        await props.getNotes(
          true,
          sortOrder === "Latest First" ? "desc" : "asc"
        );

        return response;
      }
      return response;
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("Failed to update note");
      throw error;
    } finally {
      setIsUpdatingNote(false);
    }
  };

  const handleClearForm = () => {
    resetFormState();
  };

  // const sortedNotes = React.useMemo(() => {
  //   const sortMultiplier = sortOrder === "Latest First" ? -1 : 1;
  //   return [...props.fetchedNotes].sort(
  //     (a, b) =>
  //       sortMultiplier *
  //       (new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  //   );
  // }, [props.fetchedNotes, sortOrder]);

  return (
    <Modal
      show={props.addNotesModal}
      modalClassName={`${classes.container} ${classes.container_60}`}
    >
      <ModalHeading
        heading="Patient Notes"
        subHeading={`Patient Name: ${props.patientData?.name}`}
        customClass={classes.header}
        customCloseClass={classes.close_btn}
        closeIcon="close-cross"
        onClose={props.onCloseModal}
      />

      <div className={`${classes.inner_container} displayflex`}>
        <div
          className={`displayflex displayflexCol gap-2 ${classes.left_section}`}
        >
          <div className="displayflex alignCenter justrifySpaceBetween">
            <h3 className={classes.sub_title}>Saved Notes</h3>
            <div className={classes.filter_select}>
              <select value={sortOrder} onChange={handleSortChange}>
                <option value="Latest First">Latest First</option>
                <option value="Oldest First">Oldest First</option>
              </select>
            </div>
          </div>

          <div
            ref={notesContainerRef}
            className={classes.notes_container}
            onScroll={handleScroll}
          >
            {isAddingNote && (
              <div className={classes.addingNote}>
                <div className={classes.loadingSpinner}></div>
                <p className={classes.loadingText}>Adding note...</p>
              </div>
            )}

            {isUpdatingNote && (
              <div className={classes.addingNote}>
                <div className={classes.loadingSpinner}></div>
                <p className={classes.loadingText}>Updating note...</p>
              </div>
            )}

            {props.getNotesLoading && props.fetchedNotes.length === 0 ? (
              <>
                <NoteSkeleton />
                <NoteSkeleton />
                <NoteSkeleton />
              </>
            ) : props.fetchedNotes.length > 0 ? (
              <>
                {props.fetchedNotes.map((note, index) => {
                  const noteCardStateClass = `${
                    note.noteCategory === "clinical"
                      ? classes.note_card_clinical
                      : note.noteCategory === "non-clinical"
                      ? classes.note_card_non_clinical
                      : note.noteCategory === "timer-summary"
                      ? classes.note_card_timer_summary
                      : ""
                  } ${
                    props.noteModalStates.selectedNoteId === note._id
                      ? classes.note_card_selected
                      : ""
                  }`;

                  const noteCardOpacity =
                    isUpdatingNote || isAddingNote
                      ? classes.noteCardDisabled
                      : classes.noteCardEnabled;

                  return (
                    <div
                      key={note._id || index}
                      className={`${classes.note_card_small} ${noteCardStateClass} ${noteCardOpacity}`}
                      onClick={() => handleNoteClick(note)}
                    >
                      <div className="displayflex gap-1 alignCenter justrifySpaceBetween">
                        <p className={classes.note_title}>
                          {note.isImportant === "yes" && (
                            <svg
                              className={classes.importantNoteIcon}
                              style={{ marginRight: "0.5rem" }}
                            >
                              <use xlinkHref="/assets/sprite.svg#grey-star-outline1" />
                            </svg>
                          )}
                          {note.noteCategory
                            ? note.noteCategory === "non-clinical"
                              ? "Internal Note"
                              : note.noteCategory === "timer-summary"
                              ? "Timer Summary"
                              : `${note.noteCategory
                                  .charAt(0)
                                  .toUpperCase()}${note.noteCategory.slice(
                                  1
                                )} Note`
                            : "N/A"}
                        </p>
                        <div className="displayflex gap-1 alignCenter">
                          <div className="displayflex flex-column">
                            <p className={classes.note_date}>
                              {formatDate(note.updatedAt || note.createdAt)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteNoteModal({ status: true, data: note });
                            }}
                            className={classes.iconButton}
                            disabled={
                              deleteNoteLoading ||
                              isUpdatingNote ||
                              isAddingNote
                            }
                            type="button"
                          >
                            <svg className={classes.iconDelete}>
                              <use xlinkHref="/assets/sprite.svg#icon-delete" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      {/* Display note type below Clinical Note for clinical notes */}
                      {note.noteCategory === "clinical" && note.type && (
                        <p className={classes.note_type_display}>
                          {note.type
                            .split("-")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")}
                        </p>
                      )}
                      <div
                        className={`${classes.note_desc} ${classes.note_desc_truncate}`}
                        dangerouslySetInnerHTML={{
                          __html: note.richText || "No notes available",
                        }}
                      />
                    </div>
                  );
                })}
                {props.getNotesLoading && (
                  <div className={classes.loadingMore}>
                    <div className={classes.loadingSpinner}></div>
                    <p className={classes.loadingText}>Loading more notes...</p>
                  </div>
                )}
              </>
            ) : (
              <div className={classes.no_notes}>
                <p>No notes available for this patient</p>
              </div>
            )}
          </div>
        </div>

        <div className={classes.right_section}>
          <ModalForm
            customClass={classes.modalForm}
            onSubmit={handleUpdateNote}
          >
            <div className={classes.formSection}>
              <div className={classes.radioGroup}>
                <RadioButton
                  label="Clinical Notes"
                  name="noteType"
                  checked={
                    props.noteModalStates.noteCategory.value === "clinical"
                  }
                  onChange={() => {
                    props.setNoteModalStates((prevVal) => ({
                      ...prevVal,
                      noteCategory: { value: "clinical", error: "" },
                    }));
                  }}
                  disable={
                    isUpdatingNote ||
                    isAddingNote ||
                    props.triggeredBy === "Stop"
                  }
                />

                <RadioButton
                  label="Internal Notes"
                  name="noteType"
                  checked={
                    props.noteModalStates.noteCategory.value === "non-clinical"
                  }
                  onChange={() => {
                    props.setNoteModalStates((prevVal) => ({
                      ...prevVal,
                      noteCategory: { value: "non-clinical", error: "" },
                    }));
                    // Reset note type selection when switching to non-clinical
                    setSelectedNoteType("");
                    setSelectedNoteLabel("Select a Note Type");
                  }}
                  disable={
                    isUpdatingNote ||
                    isAddingNote ||
                    props.triggeredBy === "Stop"
                  }
                />

                {props.triggeredBy === "Stop" && (
                  <RadioButton
                    label="Timer Summary"
                    name="noteType"
                    checked={
                      props.noteModalStates.noteCategory.value ===
                      "timer-summary"
                    }
                    onChange={() => {
                      props.setNoteModalStates((prevVal) => ({
                        ...prevVal,
                        noteCategory: { value: "timer-summary", error: "" },
                      }));
                    }}
                  />
                )}
              </div>
              {props.noteModalStates.noteCategory.error && (
                <p className={classes.errorText}>
                  {props.noteModalStates.noteCategory.error}
                </p>
              )}
            </div>

            <div className={classes.quillSection}>
              <div className={classes.formRow}>
                <div className={classes.noteTypeSelectContainer}>
                  <CustomDropdown
                    options={props.noteTypeOptions}
                    selectedValue={selectedNoteType}
                    selectedLabel={selectedNoteLabel}
                    onSelect={(key, label) => {
                      console.log("Selected note type:", key, label);
                      setSelectedNoteType(key);
                      setSelectedNoteLabel(label);
                    }}
                    placeholder="Select a Note Type"
                    disabled={
                      props.noteModalStates.noteCategory.value ===
                        "non-clinical" ||
                      props.noteTypesLoading ||
                      isUpdatingNote ||
                      isAddingNote ||
                      props.triggeredBy === "Stop"
                    }
                    loading={props.noteTypesLoading}
                    error={
                      props.noteTypesError ? "Failed to load note types" : ""
                    }
                  />
                </div>
                <div className={classes.flagCheckboxContainer}>
                  <label className={classes.flagCheckboxLabel}>
                    <input
                      type="checkbox"
                      className={classes.flagCheckbox}
                      checked={isImportant}
                      onChange={handleImportantChange}
                      disabled={isUpdatingNote || isAddingNote}
                    />
                    <span>Flag as important on report</span>
                  </label>
                </div>
              </div>
              <ReactQuill
                value={props.noteModalStates.noteContent.value}
                onChange={(value) =>
                  props.setNoteModalStates((prevVal) => ({
                    ...prevVal,
                    noteContent: {
                      value,
                      error: "",
                      originalValue:
                        prevVal.noteContent.originalValue !== undefined
                          ? prevVal.noteContent.originalValue
                          : value,
                    },
                  }))
                }
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Enter your notes here..."
                readOnly={isUpdatingNote || isAddingNote}
              />
              {props.noteModalStates.noteContent.error && (
                <p className={classes.errorText}>
                  {props.noteModalStates.noteContent.error}
                </p>
              )}
            </div>

            <div className={classes.phoneCallRow}>
              <label className={classes.phoneCallLabel}>
                <input
                  type="checkbox"
                  id="phoneCall"
                  className={classes.phoneCallCheckbox}
                  checked={isPhoneCall}
                  onChange={handlePhoneCallChange}
                  disabled={
                    props.noteModalStates.selectedNoteId
                      ? originalIsPhoneCall
                      : false
                  }
                />
                Phone Call
              </label>
              {props.noteModalStates.selectedNoteId && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  {/* Created By */}
                  <div className={classes.lastUpdatedText}>
                    {(() => {
                      if (props.noteModalStates.selectedNoteId) {
                        const selectedNote = props.fetchedNotes.find(
                          (note) =>
                            note._id === props.noteModalStates.selectedNoteId
                        );
                        if (selectedNote?.createdBy?.name) {
                          const clinicianName = selectedNote?.createdBy?.name;
                          return `Created by: ${clinicianName}`;
                        }
                      }
                    })()}
                  </div>

                  {/* Updated By */}
                  <div className={classes.lastUpdatedText}>
                    {(() => {
                      if (props.noteModalStates.selectedNoteId) {
                        const selectedNote = props.fetchedNotes.find(
                          (note) =>
                            note._id === props.noteModalStates.selectedNoteId
                        );
                        // Updated to use clinicianId.name from the API response
                        const clinicianName = selectedNote?.clinicianId?.name;
                        return `Updated by: ${clinicianName}`;
                      }

                      // if (props.fetchedNotes.length > 0) {
                      //   // Updated to use clinicianId.name from the API response
                      //   const clinicianName =
                      //     props.fetchedNotes[0]?.clinicianId?.name;
                      //   return `Updated by: ${clinicianName}`;
                      // }
                    })()}
                  </div>
                </div>
              )}
            </div>
            <div className={classes.buttonContainer}>
              {/* Show Add New button when editing an existing note */}
              {props.noteModalStates.selectedNoteId && (
                <Button
                  label="Add New"
                  type="button"
                  span={false}
                  variant="blue"
                  customClass={classes.btn}
                  onClick={handleClearForm}
                  disabled={isUpdatingNote || isAddingNote}
                />
              )}

              {props.noteModalStates.selectedNoteId && (
                <Button
                  label={isUpdatingNote ? "Updating..." : "Update Note"}
                  type="button"
                  span={false}
                  disabled={props.saveNoteLoading || isUpdatingNote}
                  variant="blue"
                  customClass={classes.btn}
                  onClick={handleUpdateNote}
                />
              )}
              {!props.noteModalStates.selectedNoteId && (
                <Button
                  label={isAddingNote ? "Saving..." : "Save Note"}
                  type="button"
                  span={false}
                  disabled={props.saveNoteLoading || isAddingNote}
                  variant="blue"
                  customClass={classes.btn}
                  onClick={handleUpdateNote}
                />
              )}
            </div>
          </ModalForm>
        </div>
      </div>

      <Modal show={deleteNoteModal.status}>
        <ModalHeading heading="Delete Note" />
        <ModalForm
          customClass={classes.deleteModalForm}
          onSubmit={handleDeleteConfirm}
        >
          <InputsSection customClass={classes.deleteInputSection}>
            <p className="primaryText">
              Are you sure you want to delete this note?
              <br />
              <br />
              <span className="dangerText">This action cannot be undone.</span>
            </p>
          </InputsSection>

          <ModalButtons
            submitButtonLabel="Delete Note"
            cancelButtonLabel="Cancel"
            onCancel={() => setDeleteNoteModal({ status: false })}
          />
        </ModalForm>
      </Modal>
    </Modal>
  );
};

export default AddNotesModal;
